// frontend/src/hooks/useCategories.js
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api';

/**
 * Custom hook to fetch and manage categories
 */
export const useCategories = () => {
    const [categories, setCategories] = useState([]);
    const [categoryMap, setCategoryMap] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch categories from API
    const fetchCategories = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await axios.get(
                `${API_CONFIG.BASE_URL}/api/v1/categories/categories/?page=1&page_size=20`
            );

            if (response.data && response.data.categories) {
                const fetchedCategories = response.data.categories;
                setCategories(fetchedCategories);

                // Build category map for quick lookup by ID
                const map = {};
                fetchedCategories.forEach(category => {
                    map[category.id] = category.name;
                    // Also map string IDs for compatibility
                    map[String(category.id)] = category.name;
                });
                setCategoryMap(map);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError(err.message || 'Failed to load categories');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch categories on mount
    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Helper function to get category name by ID
    const getCategoryNameById = useCallback((id) => {
        return categoryMap[id] || categoryMap[String(id)] || `Category ${id}`;
    }, [categoryMap]);

    // Helper function to get full category object by ID
    const getCategoryById = useCallback((id) => {
        return categories.find(cat => cat.id === id || cat.id === Number(id)) || null;
    }, [categories]);

    return {
        categories,
        categoryMap,
        isLoading,
        error,
        getCategoryNameById,
        getCategoryById,
        refetch: fetchCategories
    };
};

export default useCategories;